(() => {
  "use strict";

  const getRootPath = () => {
    const root = window.Shopify?.routes?.root || window.routes?.root_url || "/";
    return root.endsWith("/") ? root : `${root}/`;
  };

  const getNativeParentKey = (item) =>
    item?.parent_relationship
      ? String(item.parent_relationship.parent_key || "")
      : "";

  const getExplicitParentKey = (item) =>
    String(item?.properties?._parent_key || "");

  const getGroupId = (item) => {
    const properties = item?.properties || {};
    return String(
      properties._addon_configuration_id || properties._addon_group_id || "",
    );
  };

  const isAddonChild = (item) => {
    const properties = item?.properties || {};
    return Boolean(
      getNativeParentKey(item) ||
      properties._gift_of ||
      properties._upsell_of ||
      properties._addon_role === "child" ||
      properties._addon_parent_variant_id ||
      properties._is_gift === "true" ||
      properties._is_gift === true ||
      properties._addon_kind === "free_gift" ||
      properties._addon_kind === "paid_addon",
    );
  };

  const isPaidAddon = (item) => {
    const properties = item?.properties || {};
    return Boolean(
      properties._upsell_of || properties._addon_kind === "paid_addon",
    );
  };

  const isFreeGift = (item) => {
    if (!item) return false;
    if (isPaidAddon(item)) return false;
    const properties = item?.properties || {};
    return Boolean(
      properties._gift_of ||
      properties._is_gift === "true" ||
      properties._is_gift === true ||
      properties._addon_kind === "free_gift" ||
      Number(item.final_price) === 0 ||
      Number(item.price) === 0,
    );
  };

  const createGroupId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();

    if (window.crypto?.getRandomValues) {
      const bytes = window.crypto.getRandomValues(new Uint8Array(16));
      return Array.from(bytes, (byte) =>
        byte.toString(16).padStart(2, "0"),
      ).join("");
    }

    return `addon-${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2)}`;
  };

  const groupGiftItems = (items, options = {}) => {
    if (!Array.isArray(items) || items.length < 2) return items;

    const parentItem = items[0];
    const parentVariantId = Number(parentItem?.id);
    if (!Number.isFinite(parentVariantId) || parentVariantId <= 0) return items;

    const groupId = String(options.groupId || createGroupId());
    const configurationId = String(options.configurationId || groupId);
    const children = items.slice(1);
    const giftIds = [];
    const upsellIds = [];

    const groupedChildren = children.map((item) => {
      const properties = { ...(item.properties || {}) };
      const isUpsell = Boolean(
        properties._upsell_of || properties._addon_kind === "paid_addon",
      );
      const childVariantId = Number(item.id);

      properties._addon_group_id = groupId;
      properties._addon_configuration_id = configurationId;
      properties._addon_role = "child";
      properties._addon_parent_variant_id = String(parentVariantId);

      if (isUpsell) {
        properties._upsell_of = String(parentVariantId);
        upsellIds.push(String(childVariantId));
      } else {
        properties._gift_of = String(parentVariantId);
        properties._is_gift = "true";
        giftIds.push(String(childVariantId));
      }

      return {
        ...item,
        id: childVariantId,
        quantity: isUpsell ? (Number(item.quantity) || 1) : (Number(parentItem.quantity) || Number(item.quantity) || 1),
        parent_id: parentVariantId,
        properties,
      };
    });

    const parentProperties = {
      ...(parentItem.properties || {}),
      _addon_group_id: groupId,
      _addon_configuration_id: configurationId,
      _addon_role: "parent",
      _addon_expected_child_count: String(groupedChildren.length),
      _addon_gift_ids: giftIds.join(","),
      _addon_upsell_ids: upsellIds.join(","),
    };

    return [
      {
        ...parentItem,
        id: parentVariantId,
        quantity: Number(parentItem.quantity) || 1,
        properties: parentProperties,
      },
      ...groupedChildren,
    ];
  };

  const findRequestedLine = (cart, bodyData) => {
    if (bodyData.line) {
      return cart.items[Number.parseInt(bodyData.line, 10) - 1] || null;
    }
    if (!bodyData.id) return null;

    const keyMatch = cart.items.find(
      (item) => String(item.key) === String(bodyData.id),
    );
    if (keyMatch) return keyMatch;

    const parentVariantMatches = cart.items.filter(
      (item) =>
        !isAddonChild(item) && String(item.variant_id) === String(bodyData.id),
    );
    if (parentVariantMatches.length === 1) return parentVariantMatches[0];

    return null;
  };

  const getMainParents = (cart) =>
    (cart?.items || []).filter((item) => !isAddonChild(item));

  const getChildParentStatus = (cart, child) => {
    if (!child || !isAddonChild(child)) return { status: "not-child", parent: null };

    const parents = getMainParents(cart);
    if (parents.length === 0) return { status: "orphan", parent: null };

    // Native Shopify parent data is authoritative. Never fall through to a
    // weaker identifier when it points to a different or missing parent.
    const nativeParentKey = getNativeParentKey(child);
    if (nativeParentKey) {
      const parent = parents.find((item) => String(item.key) === nativeParentKey);
      return parent ? { status: "linked", parent } : { status: "orphan", parent: null };
    }

    // Our product form stores the resolved parent line key for children added
    // after their parent already exists. This is less authoritative than
    // Shopify's native relationship, but stronger than a configuration ID.
    const explicitParentKey = getExplicitParentKey(child);
    if (explicitParentKey) {
      const parent = parents.find((item) => String(item.key) === explicitParentKey);
      return parent ? { status: "linked", parent } : { status: "orphan", parent: null };
    }

    // A configuration/group identifier may only resolve a child when it
    // identifies exactly one current parent line.
    const childGroupId = getGroupId(child);
    if (childGroupId) {
      const matches = parents.filter((item) => getGroupId(item) === childGroupId);
      if (matches.length === 1) return { status: "linked", parent: matches[0] };
      return matches.length === 0
        ? { status: "orphan", parent: null }
        : { status: "ambiguous", parent: null };
    }

    // Variant/product metadata is a last resort. It is only safe if exactly
    // one parent line matches; identical variants are otherwise ambiguous.
    const properties = child.properties || {};
    const sourceId =
      properties._gift_of ||
      properties._upsell_of ||
      properties._addon_parent_variant_id ||
      (child.parent_id ? String(child.parent_id) : "");

    if (!sourceId) return { status: "orphan", parent: null };

    const matches = parents.filter((item) => {
      const matchesVariant =
        String(sourceId) === String(item.variant_id) ||
        String(sourceId) === String(item.id);
      const matchesProduct =
        item.product_id && String(sourceId) === String(item.product_id);
      return matchesVariant || matchesProduct;
    });

    if (matches.length === 1) return { status: "linked", parent: matches[0] };
    return matches.length === 0
      ? { status: "orphan", parent: null }
      : { status: "ambiguous", parent: null };
  };

  const doesChildBelongToParent = (cart, child, parent) => {
    if (!child || !parent || String(child.key || "") === String(parent.key || "")) {
      return false;
    }
    const relationship = getChildParentStatus(cart, child);
    return (
      relationship.status === "linked" &&
      String(relationship.parent.key) === String(parent.key)
    );
  };

  const findChildrenForParent = (cart, parent) => {
    if (!cart?.items || !parent) return [];
    return cart.items.filter(
      (item) =>
        isAddonChild(item) && doesChildBelongToParent(cart, item, parent),
    );
  };

  const readRequestBody = (body) => {
    if (typeof body === "string") return JSON.parse(body);
    if (typeof FormData !== "undefined" && body instanceof FormData) {
      return Object.fromEntries(body.entries());
    }
    return body;
  };

  const prepareCartChangeRequest = async (url, options = {}) => {
    let bodyData;
    try {
      bodyData = readRequestBody(options.body);
    } catch (error) {
      console.error("Unable to read the cart change request", error);
      return { url, options };
    }

    if (!bodyData || bodyData.quantity === undefined) {
      return { url, options };
    }

    const targetQty = Number.parseInt(bodyData.quantity, 10);
    if (Number.isNaN(targetQty)) {
      return { url, options };
    }

    const cartResponse = await window.fetch(`${getRootPath()}cart.js`, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Cache-Control": "no-cache",
      },
    });
    if (!cartResponse.ok) return { url, options };

    const cart = await cartResponse.json();
    const parent = findRequestedLine(cart, bodyData);
    if (!parent) return { url, options };

    // If an addon child itself is updated or removed, allow normal line change
    if (isAddonChild(parent)) {
      return { url, options };
    }

    if (targetQty === 0) {
      // Identify surviving main (non-addon) parent products
      const remainingParents = cart.items.filter(
        (item) => !isAddonChild(item) && String(item.key) !== String(parent.key),
      );

      const updates = { [parent.key]: 0 };

      // RULE 1: If NO main parent products remain, remove ALL items (zero addons left)
      if (remainingParents.length === 0) {
        cart.items.forEach((item) => {
          updates[item.key] = 0;
        });
      } else {
        // RULE 2: If surviving parents remain:
        // - Remove children belonging to this parent
        // - Also remove any orphaned children that do not belong to ANY surviving parent
        cart.items.forEach((item) => {
          if (String(item.key) === String(parent.key)) return;
          if (!isAddonChild(item)) return;

          // Belongs to the parent being removed
          if (doesChildBelongToParent(cart, item, parent)) {
            updates[item.key] = 0;
            return;
          }

          // Only remove confirmed orphans.
          const relationship = getChildParentStatus(cart, item);
          if (relationship.status === "orphan") {
            updates[item.key] = 0;
          }
        });
      }

      const nextBody = { updates };
      if (bodyData.sections) nextBody.sections = bodyData.sections;
      if (bodyData.sections_url) nextBody.sections_url = bodyData.sections_url;

      return {
        url: `${getRootPath()}cart/update.js`,
        options: {
          ...options,
          headers: {
            ...(options.headers || {}),
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify(nextBody),
        },
      };
    }

    // targetQty > 0: Main product quantity is increasing or decreasing.
    // Automatically scale the quantity of free gifts linked to this parent.
    // Paid add-ons are NOT scaled.
    const children = findChildrenForParent(cart, parent);
    const freeGifts = children.filter((child) => isFreeGift(child));

    if (freeGifts.length === 0) {
      return { url, options };
    }

    const updates = {
      [parent.key]: targetQty,
    };
    freeGifts.forEach((gift) => {
      updates[gift.key] = targetQty;
    });

    const nextBody = { updates };
    if (bodyData.sections) nextBody.sections = bodyData.sections;
    if (bodyData.sections_url) nextBody.sections_url = bodyData.sections_url;

    return {
      url: `${getRootPath()}cart/update.js`,
      options: {
        ...options,
        headers: {
          ...(options.headers || {}),
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(nextBody),
      },
    };
  };

  const prepareCartRemovalRequest = prepareCartChangeRequest;

  window.FairwhaleCartAddons = {
    createGroupId,
    getChildParentStatus,
    getExplicitParentKey,
    doesChildBelongToParent,
    findChildrenForParent,
    getNativeParentKey,
    groupGiftItems,
    isAddonChild,
    isFreeGift,
    isPaidAddon,
    prepareCartChangeRequest,
    prepareCartRemovalRequest,
  };
  window.__BY_prepareCartRemovalRequest = prepareCartChangeRequest;
})();
